import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDismissableMenu } from './useDismissableMenu.js';
import { useCurrentUser } from './CurrentUserContext.jsx';
import { rbac } from '@dha-nexus/shared';
import { useOperatorMode } from '../lib/operatorMode.js';
import { useClientConfig } from '../lib/clientConfig.js';
import {
  IconSearch,
  IconBell,
  IconSparkles,
  IconChevron,
  IconClose,
} from '../components/icons.jsx';
import { PrimeBrandMark } from './PrimeBrandMark.jsx';
import { FeedbackLauncher } from '../features/feedbackMarkup/FeedbackLauncher.jsx';
import { personaKeyFromRoles, primaryRoleLabel } from '../lib/personaKey.js';
import { resolveLanding } from '../lib/onboardingState.js';
import { useDemoReset, useDemoResetEligible } from '../features/demo/useDemoReset.js';
import { DemoResetConfirm } from '../features/demo/DemoResetConfirm.jsx';

// Fire the shared tour-start event consumed by the parallel tour engine (WP5).
// Tour ids: 'orientation' | 'j1-lifecycle' | 'j2-data-to-decision' | 'j3-first-week'.
function dispatchStartTour(tourId) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('prime:start-tour', { detail: { tourId } }));
  }
}

// Leftmost mobile-shell control: opens the off-canvas nav drawer (PRIME-MOB-UX-001).
function IconHamburger({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function authConfigValue(config, key, fallback) {
  if (config?.auth && key in config.auth) return config.auth[key];
  if (config?.flags && key in config.flags) return config.flags[key];
  return fallback;
}

export function TopBar({
  demoMode,
  onToggleDemoMode,
  tooltipsDisabled,
  onToggleTooltipsDisabled,
  // PRIME-MOB-UX-001: mobile-shell mode. When true the top bar renders the
  // single-row phone layout — a hamburger that opens the nav drawer, a compact
  // brand mark, a collapsed search that expands to a full-width overlay, and the
  // Help + operator Workspace controls relocated into the avatar menu.
  mobile = false,
  onOpenDrawer,
  hamburgerRef,
}) {
  const {
    user,
    roles,
    unreadNotificationCount,
    setPersona,
    logout = async () => {},
  } = useCurrentUser();
  const clientConfig = useClientConfig();
  const navigate = useNavigate();
  const location = useLocation();
  // PRIME-SHELL-UX-003: every top-bar-owned dropdown shares one dismissal
  // contract via useDismissableMenu — outside-click (no click-through), Escape
  // with focus return, route-change close, mutual exclusion (opening one closes
  // the others), and a calm ~4s inactivity auto-close that pauses on hover/
  // focus. Feedback launcher menu is intentionally NOT adopted (it owns its own
  // proven outside-dismissal and lives outside this file's scope).
  const demoMenu = useDismissableMenu();
  const helpMenu = useDismissableMenu();
  const personaMenu = useDismissableMenu();
  const profileMenu = useDismissableMenu();
  const [searchQuery, setSearchQuery] = useState('');

  // Mobile-shell search (locked decision 3): collapsed to an icon that expands
  // into a full-width overlay input, dismissed by Escape/blur/close, focus
  // returning to the toggle. Desktop keeps the always-open center search.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchToggleRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  useEffect(() => {
    if (mobileSearchOpen) mobileSearchInputRef.current?.focus();
  }, [mobileSearchOpen]);
  // Any route change collapses the overlay so it never lingers over a new page.
  useEffect(() => {
    setMobileSearchOpen(false);
  }, [location.pathname]);
  // Leaving mobile mode collapses the overlay so the desktop search is canonical.
  useEffect(() => {
    if (!mobile) setMobileSearchOpen(false);
  }, [mobile]);
  const closeMobileSearch = ({ restoreFocus = true } = {}) => {
    setMobileSearchOpen(false);
    if (restoreFocus) mobileSearchToggleRef.current?.focus();
  };

  // PRIME-SEARCH-DEF-001 (locked decision 4): while on /search, keep the
  // top-bar input showing the active query so the user can see what was searched
  // and re-run it. Keyed on the URL query, so it never fights in-progress typing
  // (the URL only changes on submit/navigation).
  const onSearchRoute = location.pathname === '/search';
  const urlSearchQuery = onSearchRoute ? new URLSearchParams(location.search).get('q') || '' : '';
  useEffect(() => {
    if (onSearchRoute) setSearchQuery(urlSearchQuery);
  }, [onSearchRoute, urlSearchQuery]);

  // Feature G — badge "pop": when the live poller raises the unread count, the
  // bell badge briefly pulses to draw the eye. We compare against the previous
  // render's count; only an *increase* pulses (a decrement from reading a
  // notification must not animate).
  const [badgePulse, setBadgePulse] = useState(false);
  const prevUnreadRef = useRef(unreadNotificationCount || 0);
  useEffect(() => {
    const previous = prevUnreadRef.current;
    prevUnreadRef.current = unreadNotificationCount || 0;
    if ((unreadNotificationCount || 0) > previous) {
      setBadgePulse(true);
      const timer = setTimeout(() => setBadgePulse(false), 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [unreadNotificationCount]);

  // Operator mode (P1-E8-1 / AC-SHELL-4): the presentation-overlay toggle is
  // demo chrome rendered only while operator mode is active; the toggle itself
  // is offered only to System Admins.
  const operatorMode = useOperatorMode();

  // Demo reset is now a one-click, server-flag-driven action (GET
  // /api/config/client → flags.destructiveDemoResetEnabled) gated by role, NOT
  // by operator mode. The eligibility + flow live in the shared useDemoReset
  // module so the topbar and the Help Center card share one implementation.
  const canResetDemo = useDemoResetEligible();
  const demoReset = useDemoReset();
  const overlayNoun = 'presentation overlay';
  // PRIME-SHELL-UX-002 (AC-01 / AC-04): the Workspace control is an operator/
  // demo entry point, not a standard-user control. Render it only when at least
  // one gated capability is actually available to this session — the same three
  // capabilities its menu items are gated by. A standard production user
  // therefore sees exactly Search, Feedback, Help, Notifications, avatar, while
  // operators/admins keep the full menu unchanged. The rich-tooltips preference
  // it also hosts now has a canonical home in Settings; the item stays inside
  // this menu for operators as a fast path (no capability is removed).
  const showWorkspaceControls = operatorMode.canEnable || operatorMode.enabled || canResetDemo;
  const personaShortcutsEnabled = authConfigValue(clientConfig, 'personaShortcutsEnabled', true);
  // Giving feedback is for every authenticated persona (W1B regate): the
  // launcher tracks ROUTE_PERMISSIONS['/feedback'] (empty list = any signed-in
  // user), the same permission that guards the tester feedback surfaces and
  // the packages API. Only the governed triage QUEUE keeps its role gate.
  const canUseFeedback =
    Boolean(user) && rbac.hasAnyRole(roles, rbac.ROUTE_PERMISSIONS['/feedback'] || []);

  const personaKey = personaKeyFromRoles(roles);
  const personaDef = personaKey ? rbac.PERSONA_DISPLAY[personaKey] : null;

  async function switchTo(p) {
    const res = await setPersona(p);
    personaMenu.close();
    if (res?.defaultLanding) {
      // A persona the SME has not yet welcomed lands on /home first (onboarding
      // WP5); otherwise the server's default landing is honored unchanged.
      navigate(resolveLanding(res.defaultLanding, res.actorUserId));
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    // Attach a monotonic submit nonce to the navigation state. When the query is
    // unchanged (same-term resubmit) the URL does not change and /search's
    // key-driven fetch would not refire; the nonce lets SearchResultsPage detect
    // the resubmit and re-run the search (PRIME-SEARCH-DEF-001, ticket §7 lead 2).
    navigate(`/search?q=${encodeURIComponent(q)}`, { state: { searchSubmit: Date.now() } });
  }

  async function handleSignOut() {
    personaMenu.close();
    profileMenu.close();
    await logout();
    navigate('/login');
  }

  // The operator Workspace menu items. Shared so the desktop Workspace dropdown
  // and the mobile avatar menu render the exact same gated controls with no
  // forked logic (locked decision 3). `close` is the menu these live in.
  function renderWorkspaceMenuItems(close) {
    return (
      <>
        <button
          className="persona-menu-item"
          role="menuitem"
          onClick={() => {
            onToggleTooltipsDisabled?.();
            close();
          }}
        >
          <div>
            <div className="persona-menu-label">
              {tooltipsDisabled ? 'Turn rich tooltips on' : 'Turn rich tooltips off'}
            </div>
            <div className="persona-menu-summary">
              Disable hover and click explainer cards across charts and admin screens.
            </div>
          </div>
        </button>
        {operatorMode.canEnable ? (
          <button
            className="persona-menu-item"
            role="menuitem"
            data-testid="operator-mode-toggle"
            onClick={() => {
              if (operatorMode.enabled) {
                operatorMode.disable();
              } else {
                operatorMode.enable();
              }
              close();
            }}
          >
            <div>
              <div className="persona-menu-label">
                {operatorMode.enabled ? 'Exit operator mode' : 'Enter operator mode'}
              </div>
              <div className="persona-menu-summary">
                Demo and presentation controls for operators — entry is audited.
              </div>
            </div>
          </button>
        ) : null}
        {operatorMode.enabled ? (
          <button
            className="persona-menu-item"
            role="menuitem"
            data-testid="demo-presentation-overlay-toggle"
            onClick={() => {
              onToggleDemoMode?.();
              close();
            }}
          >
            <div>
              <div className="persona-menu-label">
                {demoMode ? `Hide ${overlayNoun}` : `Show ${overlayNoun}`}
              </div>
              <div className="persona-menu-summary">
                Visual chrome around live data — toggle for screenshots.
              </div>
            </div>
          </button>
        ) : null}
        {canResetDemo ? (
          <button
            className="persona-menu-item"
            role="menuitem"
            data-testid="demo-reset"
            onClick={() => {
              demoReset.requestReset();
              close();
            }}
          >
            <div>
              <div className="persona-menu-label">{demoReset.copy.menuLabel}</div>
              <div className="persona-menu-summary">{demoReset.copy.menuSummary}</div>
            </div>
          </button>
        ) : null}
      </>
    );
  }

  // The Help & getting started menu items. Shared so the desktop Help dropdown
  // and the mobile avatar menu render identical entries (locked decision 3 —
  // Help moves into the avatar menu at mobile widths).
  function renderHelpMenuItems(close) {
    return (
      <>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-getting-started"
          onClick={() => {
            close();
            navigate('/help');
          }}
        >
          <div>
            <div className="persona-menu-label">Help &amp; getting started</div>
            <div className="persona-menu-summary">
              Tours, glossary, capability map, and the user guide in one place.
            </div>
          </div>
        </button>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-tour-orientation"
          onClick={() => {
            close();
            dispatchStartTour('orientation');
          }}
        >
          <div>
            <div className="persona-menu-label">Take the 2-minute tour</div>
            <div className="persona-menu-summary">
              A quick orientation to how the workspace fits together.
            </div>
          </div>
        </button>
        <div className="persona-menu-group-label" data-testid="help-journey-group">
          Journey tours
        </div>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-tour-j1"
          onClick={() => {
            close();
            dispatchStartTour('j1-lifecycle');
          }}
        >
          <div>
            <div className="persona-menu-label">Journey 1 — Lifecycle</div>
          </div>
        </button>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-tour-j2"
          onClick={() => {
            close();
            dispatchStartTour('j2-data-to-decision');
          }}
        >
          <div>
            <div className="persona-menu-label">Journey 2 — Data to decision</div>
          </div>
        </button>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-tour-j3"
          onClick={() => {
            close();
            dispatchStartTour('j3-first-week');
          }}
        >
          <div>
            <div className="persona-menu-label">Journey 3 — First week</div>
          </div>
        </button>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-glossary"
          onClick={() => {
            close();
            navigate('/help/glossary');
          }}
        >
          <div>
            <div className="persona-menu-label">Glossary</div>
          </div>
        </button>
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-capability-map"
          onClick={() => {
            close();
            navigate('/help/capability-map');
          }}
        >
          <div>
            <div className="persona-menu-label">Capability map</div>
          </div>
        </button>
        <hr />
        <button
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-replay-welcome"
          onClick={() => {
            close();
            navigate('/home?welcome=1');
          }}
        >
          <div>
            <div className="persona-menu-label">Replay welcome</div>
          </div>
        </button>
        <a
          className="persona-menu-item"
          role="menuitem"
          data-testid="help-download-guide"
          href="/help/dha-prime-user-guide.pdf"
          download
          onClick={() => close()}
        >
          <div>
            <div className="persona-menu-label">Download user guide (PDF)</div>
          </div>
        </a>
      </>
    );
  }

  return (
    <header className="app-topbar" data-testid="app-topbar" role="banner">
      {/* PRIME-SHELL-UX-001: the route title, purpose line, and "About this
          page" control moved out of the topbar into the body PageHeader so each
          route shows a single page identity. On desktop the left region is a
          blank flexible spacer (it holds the grid's left track). In the mobile
          shell (PRIME-MOB-UX-001) it hosts the drawer hamburger + compact brand
          mark as the leftmost controls. */}
      <div className="topbar-left">
        {mobile ? (
          <>
            <button
              ref={hamburgerRef}
              type="button"
              className="topbar-btn topbar-hamburger m-touch-target"
              onClick={onOpenDrawer}
              aria-label="Open navigation menu"
              aria-haspopup="dialog"
              data-testid="mobile-nav-trigger"
              title="Menu"
            >
              <IconHamburger size={20} />
            </button>
            <span className="topbar-mobile-brand" data-testid="mobile-topbar-brand">
              <PrimeBrandMark variant="topbar" />
            </span>
          </>
        ) : null}
      </div>

      {mobile ? null : (
        <div className="topbar-center">
          <form className="topbar-search" onSubmit={handleSearchSubmit} role="search">
            <IconSearch size={16} />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search portfolios, programs, projects, briefings"
              aria-label="Global search"
            />
          </form>
        </div>
      )}
      <div className="topbar-right">
        {mobile ? (
          <div className="topbar-mobile-search">
            <button
              ref={mobileSearchToggleRef}
              type="button"
              className="topbar-btn topbar-search-toggle m-touch-target"
              onClick={() => setMobileSearchOpen((v) => !v)}
              aria-label="Search"
              aria-haspopup="dialog"
              aria-expanded={mobileSearchOpen}
              data-testid="mobile-search-toggle"
              title="Search"
            >
              <IconSearch size={18} />
            </button>
            {mobileSearchOpen ? (
              <div
                className="topbar-search-overlay"
                data-testid="mobile-search-overlay"
                role="search"
              >
                <form
                  className="topbar-search"
                  onSubmit={(e) => {
                    handleSearchSubmit(e);
                    if (searchQuery.trim()) closeMobileSearch({ restoreFocus: false });
                  }}
                >
                  <IconSearch size={16} />
                  <input
                    ref={mobileSearchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        closeMobileSearch();
                      }
                    }}
                    onBlur={(e) => {
                      // Dismiss on blur unless focus moved to the overlay's own
                      // close button (which handles its own dismissal).
                      if (
                        !e.currentTarget
                          .closest('.topbar-search-overlay')
                          ?.contains(e.relatedTarget)
                      ) {
                        closeMobileSearch({ restoreFocus: false });
                      }
                    }}
                    placeholder="Search portfolios, programs, projects, briefings"
                    aria-label="Global search"
                  />
                  <button
                    type="button"
                    className="topbar-search-close m-touch-target"
                    onClick={() => closeMobileSearch()}
                    aria-label="Close search"
                  >
                    <IconClose size={16} />
                  </button>
                </form>
              </div>
            ) : null}
          </div>
        ) : null}
        {/* Typed help/intake framework, commit 2: the launcher itself is no longer
            gated by VITE_ENABLE_REVIEW_TOOLS. It is now the entry point to
            "Report a problem", which every authenticated user needs — a flag named
            "review tools" must not decide whether a real user can reach the
            service desk. The SCREEN-CAPTURE group inside the menu keeps that exact
            gate; FeedbackMenu owns it. */}
        {canUseFeedback ? <FeedbackLauncher /> : null}
        {!mobile && showWorkspaceControls ? (
          <div className="demo-switch">
            <button
              ref={demoMenu.triggerRef}
              className={`topbar-btn ${demoMode || tooltipsDisabled ? 'active' : ''}`}
              onClick={demoMenu.toggle}
              aria-haspopup="menu"
              aria-expanded={demoMenu.open}
              title="Workspace controls"
            >
              <IconSparkles size={16} /> <span className="hide-sm">Workspace</span>
              {operatorMode.enabled ? (
                <span className="operator-mode-chip" data-testid="operator-mode-chip">
                  Operator
                </span>
              ) : null}
              <IconChevron size={12} />
            </button>
            {demoMenu.open && (
              <div
                ref={demoMenu.popupRef}
                className="persona-menu glass-panel"
                role="menu"
                data-testid="demo-menu"
              >
                {renderWorkspaceMenuItems(demoMenu.close)}
              </div>
            )}
          </div>
        ) : null}
        {mobile ? null : (
          <div className="help-switch">
            <button
              ref={helpMenu.triggerRef}
              className={`topbar-btn ${helpMenu.open ? 'active' : ''}`}
              onClick={helpMenu.toggle}
              aria-haspopup="menu"
              aria-expanded={helpMenu.open}
              data-testid="help-menu"
              data-tour="help-menu"
              title="Help & getting started"
            >
              <span aria-hidden style={{ fontSize: 15, fontWeight: 700, lineHeight: 1 }}>
                ?
              </span>
              <span className="hide-sm">Help</span>
              <IconChevron size={12} />
            </button>
            {helpMenu.open && (
              <div
                ref={helpMenu.popupRef}
                className="persona-menu glass-panel"
                role="menu"
                data-testid="help-menu-panel"
              >
                {renderHelpMenuItems(helpMenu.close)}
              </div>
            )}
          </div>
        )}
        <button
          className="topbar-btn"
          aria-label={
            unreadNotificationCount > 0
              ? `Notifications, ${unreadNotificationCount} unread`
              : 'Notifications'
          }
          title={
            unreadNotificationCount > 0
              ? `${unreadNotificationCount} unread notification${
                  unreadNotificationCount === 1 ? '' : 's'
                }`
              : 'No unread notifications'
          }
          onClick={() => navigate('/notifications')}
        >
          <IconBell size={16} />
          {unreadNotificationCount > 0 ? (
            <span
              className={`badge${badgePulse ? ' badge--pulse' : ''}`}
              data-testid="notification-badge"
            >
              {unreadNotificationCount}
            </span>
          ) : null}
        </button>
        {personaShortcutsEnabled ? (
          <div className="persona-switch">
            <button
              ref={personaMenu.triggerRef}
              className="topbar-persona-pill"
              onClick={personaMenu.toggle}
              aria-haspopup="menu"
              aria-expanded={personaMenu.open}
              title="Switch persona"
            >
              {personaDef ? (
                <span className={`persona-swatch ${personaDef.accent}`} aria-hidden />
              ) : null}
              <span className="hide-sm">{personaDef?.label || 'Persona'}</span>
              <IconChevron size={12} />
            </button>
            {personaMenu.open && (
              <div
                ref={personaMenu.popupRef}
                className="persona-menu glass-panel"
                role="menu"
                data-testid="persona-menu"
              >
                {Object.entries(rbac.PERSONA_DISPLAY).map(([key, def]) => (
                  <button
                    key={key}
                    className="persona-menu-item"
                    role="menuitem"
                    onClick={() => switchTo(key)}
                  >
                    <div className={`persona-swatch ${def.accent}`} />
                    <div>
                      <div className="persona-menu-label">{def.label}</div>
                      <div className="persona-menu-summary">{def.summary}</div>
                    </div>
                  </button>
                ))}
                <hr />
                <button className="persona-menu-item" onClick={() => navigate('/login')}>
                  Manage personas →
                </button>
                <button
                  className="persona-menu-item"
                  role="menuitem"
                  data-testid="persona-return-to-signin"
                  onClick={async () => {
                    // Personas replace the tester's own session; the only clean
                    // way back to their account is a fresh password sign-in
                    // with the persona session ended first (DEF-006).
                    personaMenu.close();
                    await logout();
                    navigate('/login');
                  }}
                >
                  Return to my sign-in →
                </button>
              </div>
            )}
          </div>
        ) : null}
        <div className="profile-switch">
          <button
            ref={profileMenu.triggerRef}
            className="topbar-profile-avatar"
            onClick={profileMenu.toggle}
            aria-haspopup="menu"
            aria-expanded={profileMenu.open}
            title={user?.displayName || 'Profile'}
          >
            <span className="profile-avatar-initials" aria-hidden>
              {(user?.displayName || '??').slice(0, 2)}
            </span>
            <IconChevron size={12} />
          </button>
          {profileMenu.open && (
            <div
              ref={profileMenu.popupRef}
              className="persona-menu glass-panel"
              role="menu"
              data-testid="profile-menu"
            >
              <div className="profile-menu-header">
                <div className="profile-menu-name">{user?.displayName || 'Signed in'}</div>
                {/* Guide-10 external UAT FINDING-018: one canonical role label —
                    the account's ACTUAL role (as Mission Brief shows it), with
                    the persona bucket only as a fallback. */}
                <div className="profile-menu-role">
                  {primaryRoleLabel(roles) || personaDef?.label || ''}
                </div>
              </div>
              <hr />
              <button
                className="persona-menu-item"
                role="menuitem"
                onClick={() => {
                  profileMenu.close();
                  navigate('/me');
                }}
              >
                <div>
                  <div className="persona-menu-label">My profile</div>
                  <div className="persona-menu-summary">Preferences, alignment, roles, access.</div>
                </div>
              </button>
              <button
                className="persona-menu-item"
                role="menuitem"
                data-testid="profile-my-activity"
                onClick={() => {
                  profileMenu.close();
                  navigate('/activity');
                }}
              >
                <div>
                  <div className="persona-menu-label">My activity</div>
                  <div className="persona-menu-summary">
                    The history of actions you have taken across PRIME.
                  </div>
                </div>
              </button>
              <button
                className="persona-menu-item"
                role="menuitem"
                onClick={() => {
                  profileMenu.close();
                  navigate('/settings');
                }}
              >
                <div>
                  <div className="persona-menu-label">Settings</div>
                  <div className="persona-menu-summary">Notifications and account preferences.</div>
                </div>
              </button>
              {/* PRIME-MOB-UX-001 (locked decision 3): at mobile widths the Help
                  menu and — for operators — the Workspace controls have no
                  dedicated top-bar button; they live here in the avatar menu.
                  Same items, same testids, one instance (the standalone
                  dropdowns are not rendered on mobile). */}
              {mobile ? (
                <>
                  <hr />
                  <div className="persona-menu-group-label" data-testid="profile-help-group">
                    Help &amp; getting started
                  </div>
                  {renderHelpMenuItems(profileMenu.close)}
                  {showWorkspaceControls ? (
                    <>
                      <hr />
                      <div
                        className="persona-menu-group-label"
                        data-testid="profile-workspace-group"
                      >
                        Workspace
                      </div>
                      {renderWorkspaceMenuItems(profileMenu.close)}
                    </>
                  ) : null}
                </>
              ) : null}
              <hr />
              <button className="persona-menu-item" role="menuitem" onClick={handleSignOut}>
                <div>
                  <div className="persona-menu-label">Sign out</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      {canResetDemo ? (
        <DemoResetConfirm
          open={demoReset.confirmOpen}
          resetting={demoReset.resetting}
          onCancel={demoReset.cancelReset}
          onConfirm={demoReset.performReset}
          copy={demoReset.copy}
        />
      ) : null}
    </header>
  );
}
